import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import { theme } from '@Guidelines/Theme/theme';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import formatDocumentReference from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/formatDocumentReference';
import type DocumentFieldEvidenceView from '@UI/Store/Documents/Views/DocumentFieldEvidenceView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import useSubjectDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectDocuments';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 1rem 1.125rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const referenceStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const labelStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const quoteStyle = css`
	margin: 0;
	padding-left: 0.75rem;
	border-left: 3px solid ${theme.colors.waveform};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	line-height: 1.65;
	color: ${theme.colors.inkSecondary};
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const linkStyle = css`
	align-self: flex-start;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.accent};
`;

type Props = {
	readonly evidence: DocumentFieldEvidenceView;
};

/** One field of a source document: the document it comes from, the field's label and the value
 * frozen at generation time, and a link to the document. The document is resolved against
 * the dossier's documents; the reference falls back to its id when it is absent. */
const DocumentFieldEvidenceCard: React.FC<Props> = ({ evidence }) => {
	const { subject } = useDocumentRoute();
	const { data: documents } = useSubjectDocuments(subject);
	const { documentTypes } = useAccountDocumentTypes();

	const reference = React.useMemo(() => {
		const sourceDocument = documents?.findById(evidence.documentId) ?? null;
		const typeLabel =
			sourceDocument !== null && documentTypes !== null
				? documentTypes.labelOf(sourceDocument.documentTypeId)
				: null;

		return formatDocumentReference(sourceDocument, evidence.documentId, typeLabel);
	}, [documents, documentTypes, evidence.documentId]);

	return (
		<div css={cardStyle}>
			<Eyebrow>Document source</Eyebrow>
			<p css={referenceStyle}>{reference}</p>
			<p css={labelStyle}>{evidence.label}</p>
			<p css={quoteStyle}>{evidence.value}</p>
			<p css={noteStyle}>
				Valeur au moment de la génération. Ouvrez le document source pour voir son état actuel et son
				niveau de vérification.
			</p>
			<Link css={linkStyle} to={`${buildSubjectPath(subject)}/documents/${evidence.documentId}`}>
				Ouvrir le document source
			</Link>
		</div>
	);
};

export default DocumentFieldEvidenceCard;
